/**
 * Flash error capture
 * --------------------
 * Some pages briefly flash an error toast on load (typically when a
 * Firestore query or brain call fires before Firebase auth has
 * hydrated, the rule returns permission-denied, the component catches
 * it via `toast.error(...)`, then re-renders cleanly once auth is
 * ready). The toast auto-dismisses after ~3s — by the time the user
 * opens DevTools the error is gone and nothing remains in the console.
 *
 * This module installs a passive recorder that:
 *   - Wraps `toast.error` from `sonner` so every error toast (text +
 *     stack at the call site + timestamp + page URL) is appended to
 *     `window.__flashErrors` AND mirrored to `console.warn` with a
 *     bright `[flash-error]` prefix so it survives the auto-dismiss.
 *   - Wraps `console.error` similarly (in case some code logs there
 *     instead of toasting).
 *   - Persists the latest 50 entries to `sessionStorage` under
 *     `__flashErrors` so they survive in-app navigations and
 *     can be inspected after the page settles.
 *
 * Zero behaviour change — original handlers still run. Designed for
 * dev/preview; left enabled in production so the user can debug
 * intermittent issues on their deployed app too.
 *
 * To inspect after the fact (in DevTools console):
 *
 *     copy(JSON.stringify(window.__flashErrors, null, 2));
 *     // or just:
 *     window.__flashErrors;
 *
 * Or use the convenience helper:
 *
 *     window.__dumpFlashErrors();
 */

import { toast as sonnerToast } from 'sonner';

interface FlashErrorEntry {
  /** ISO-8601 UTC timestamp captured the moment the error fired. */
  ts: string;
  /** Where it came from. */
  source: 'toast.error' | 'console.error';
  /** The (stringified) primary message. */
  message: string;
  /** Any additional args that were passed alongside the primary message. */
  details?: unknown[];
  /** Call-site stack (best-effort — V8/SpiderMonkey/JSC differ). */
  stack?: string;
  /** Page URL at the time the error was captured. */
  href: string;
  /** Tabular age relative to page mount, in seconds. */
  mountAgeSec: number;
}

const STORAGE_KEY = '__flashErrors';
const MAX_ENTRIES = 50;
const PAGE_MOUNTED_AT = (typeof performance !== 'undefined' ? performance.now() : Date.now());

declare global {
  interface Window {
    __flashErrors: FlashErrorEntry[];
    __dumpFlashErrors: () => FlashErrorEntry[];
    __clearFlashErrors: () => void;
    __flashErrorsInstalled?: boolean;
  }
}

const safeStringify = (v: unknown): string => {
  try {
    if (v == null) return String(v);
    if (typeof v === 'string') return v;
    if (v instanceof Error) return v.message || String(v);
    return JSON.stringify(v);
  } catch {
    try {
      return String(v);
    } catch {
      return '[unstringifiable]';
    }
  }
};

const append = (entry: FlashErrorEntry) => {
  try {
    if (!window.__flashErrors) window.__flashErrors = [];
    window.__flashErrors.push(entry);
    // Cap memory growth.
    if (window.__flashErrors.length > MAX_ENTRIES) {
      window.__flashErrors.splice(0, window.__flashErrors.length - MAX_ENTRIES);
    }
    // Persist so they survive in-app navigations.
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(window.__flashErrors));
  } catch {
    // sessionStorage may be unavailable (private mode) — fine to skip.
  }

  // Mirror to console so the user can scroll back later. Prefix is
  // intentionally loud so it stands out among Vite/HMR chatter.
  // eslint-disable-next-line no-console
  console.warn(
    `%c[flash-error]%c ${entry.source} · ${entry.mountAgeSec.toFixed(2)}s after mount`,
    'background:#fee;color:#a40000;font-weight:bold;padding:1px 4px;border-radius:2px;',
    'color:#a40000;',
    entry,
  );
};

const makeEntry = (
  source: FlashErrorEntry['source'],
  primary: unknown,
  rest: unknown[],
): FlashErrorEntry => {
  let stack: string | undefined;
  try {
    stack = new Error().stack?.split('\n').slice(2, 10).join('\n');
  } catch {
    /* noop */
  }
  const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return {
    ts: new Date().toISOString(),
    source,
    message: safeStringify(primary),
    details: rest.length ? rest : undefined,
    stack,
    href: typeof window !== 'undefined' ? window.location.href : '',
    mountAgeSec: (nowMs - PAGE_MOUNTED_AT) / 1000,
  };
};

export const installFlashErrorCapture = (): void => {
  if (typeof window === 'undefined') return;
  if (window.__flashErrorsInstalled) return;
  window.__flashErrorsInstalled = true;

  // Restore prior entries from this tab session.
  try {
    const prev = sessionStorage.getItem(STORAGE_KEY);
    window.__flashErrors = prev ? JSON.parse(prev) : [];
  } catch {
    window.__flashErrors = [];
  }

  // 1. Wrap `toast.error`. `sonner` exports `toast` as a callable with
  //    method properties — we replace `.error` while keeping every
  //    other method untouched.
  try {
    const origError = sonnerToast.error.bind(sonnerToast);
    (sonnerToast as any).error = (message: any, ...rest: any[]) => {
      try {
        append(makeEntry('toast.error', message, rest));
      } catch {
        /* never let the wrapper crash the real error path */
      }
      return origError(message, ...rest);
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[flash-error] failed to wrap toast.error', e);
  }

  // 2. Wrap `console.error`. Same idea — fully transparent.
  try {
    const origConsoleError = console.error.bind(console);
    console.error = function patchedConsoleError(...args: any[]) {
      try {
        const [primary, ...rest] = args;
        append(makeEntry('console.error', primary, rest));
      } catch {
        /* noop */
      }
      return origConsoleError(...args);
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[flash-error] failed to wrap console.error', e);
  }

  // Helpers for the user to call from DevTools.
  window.__dumpFlashErrors = () => {
    const list = window.__flashErrors || [];
    // eslint-disable-next-line no-console
    console.table(
      list.map((e) => ({
        ts: e.ts,
        ageSec: e.mountAgeSec.toFixed(2),
        src: e.source,
        msg: e.message.slice(0, 140),
        href: e.href.split('/').pop(),
      })),
    );
    return list;
  };
  window.__clearFlashErrors = () => {
    window.__flashErrors = [];
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  };

  // eslint-disable-next-line no-console
  console.log(
    '%c[flash-error]%c capture installed — type %cwindow.__dumpFlashErrors()%c in DevTools to inspect.',
    'background:#eef;color:#04a;font-weight:bold;padding:1px 4px;border-radius:2px;',
    'color:#04a;',
    'font-family:monospace;background:#eee;padding:0 4px;',
    'color:#04a;',
  );
};
